import { formatDateLong, formatTime } from '@/shared/formatters';
import { removeTelefoneMask } from '@/shared/utils/contato';
import type {
  BolhaWhatsappCertificado,
  ConversaWhatsappCertificado,
  MensagemWhatsappCertificado,
  RespostaWhatsappCertificado,
} from '../types/certificadoAcessosTypes';

const STATUS_DA_RESPOSTA = 'Recebida';

/**
 * O envio grava o número sem `+` e a conversa com ele (`5518…` × `+5518…`): o casamento é pelos
 * dígitos, ou a resposta abriria uma conversa à parte para o mesmo telefone.
 */
function chaveDaConversa(parteArbitralId: string, contato: string): string {
  return `${parteArbitralId}:${removeTelefoneMask(contato)}`;
}

function bolhaEnviada(
  mensagem: MensagemWhatsappCertificado,
  destinatario: MensagemWhatsappCertificado['destinatarios'][number],
  indiceMensagem: number,
): BolhaWhatsappCertificado {
  // Rótulo e ordem saem do MESMO horário, o do envio a esta parte: o do grupo é o da primeira
  // réplica, e imprimi-lo numa bolha ordenada por outro horário faria a conversa parecer fora de ordem.
  const { enviadoEm } = destinatario;
  return {
    chave: `${destinatario.chave}:${indiceMensagem}`,
    direcao: 'ENVIADA',
    corpo: mensagem.corpo,
    dataLabel: enviadoEm ? formatDateLong(enviadoEm) : mensagem.dataLabel,
    horaLabel: enviadoEm ? formatTime(enviadoEm) : mensagem.horaLabel,
    instante: enviadoEm,
    idMensagem: destinatario.idMensagem,
    statusEntrega: destinatario.statusEntrega,
    statusLabel: destinatario.statusLabel,
    sucesso: destinatario.sucesso,
    ressalva: null,
  };
}

function bolhaRecebida(
  resposta: RespostaWhatsappCertificado,
  chaveConversa: string,
  indice: number,
): BolhaWhatsappCertificado {
  return {
    chave: `${chaveConversa}:resposta:${indice}`,
    direcao: 'RECEBIDA',
    corpo: resposta.corpo,
    dataLabel: resposta.dataLabel,
    horaLabel: resposta.horaLabel,
    instante: resposta.recebidaEm,
    idMensagem: resposta.idMensagem,
    // A trilha registrou a resposta: ela chegou. O ícone de entrega não é desenhado nesta bolha.
    statusEntrega: 'DELIVERED',
    statusLabel: STATUS_DA_RESPOSTA,
    sucesso: true,
    ressalva: resposta.ressalva,
  };
}

/**
 * A conversa em que a resposta entra: a do mesmo número da mesma parte. Sem número (a mensagem de
 * origem não foi encontrada), só entra numa conversa existente se a parte tiver UMA — com duas, pôr
 * a resposta numa delas afirmaria um número que o documento não sabe.
 */
function conversaDaResposta(
  conversas: Map<string, ConversaWhatsappCertificado>,
  resposta: RespostaWhatsappCertificado,
  de: string,
): ConversaWhatsappCertificado {
  const chave = chaveDaConversa(resposta.parteArbitralId, resposta.contato);
  const mesmoNumero = conversas.get(chave);
  if (mesmoNumero) return mesmoNumero;

  const daParte = [...conversas.values()].filter((c) =>
    c.chave.startsWith(`${resposta.parteArbitralId}:`),
  );
  const unicaDaParte = daParte.length === 1 ? daParte[0] : undefined;
  if (!resposta.contato && unicaDaParte) return unicaDaParte;

  const nova: ConversaWhatsappCertificado = {
    chave,
    de,
    nome: resposta.nome,
    contato: resposta.contato,
    bolhas: [],
  };
  conversas.set(chave, nova);
  return nova;
}

function instanteEmMs(bolha: BolhaWhatsappCertificado): number | null {
  return bolha.instante ? Date.parse(bolha.instante) : null;
}

/**
 * Intercala as respostas entre as mensagens enviadas pela hora. A ordem das enviadas não muda (é a
 * de envio, já garantida pelo mapper), e enviada sem horário não serve de marco: a resposta só é
 * posta antes de uma enviada cujo horário é posterior ao dela.
 */
function intercalarPorHorario(
  enviadas: BolhaWhatsappCertificado[],
  recebidas: BolhaWhatsappCertificado[],
): BolhaWhatsappCertificado[] {
  const ordenadas = [...recebidas].sort((a, b) => (instanteEmMs(a) ?? 0) - (instanteEmMs(b) ?? 0));
  const resultado: BolhaWhatsappCertificado[] = [];
  let proxima = 0;
  for (const enviada of enviadas) {
    const instanteEnviada = instanteEmMs(enviada);
    while (
      proxima < ordenadas.length &&
      instanteEnviada !== null &&
      (instanteEmMs(ordenadas[proxima]) ?? 0) < instanteEnviada
    ) {
      resultado.push(ordenadas[proxima]);
      proxima += 1;
    }
    resultado.push(enviada);
  }
  return [...resultado, ...ordenadas.slice(proxima)];
}

/**
 * Reagrupa a réplica de WhatsApp de "uma bolha por conteúdo" para "uma conversa por número", com as
 * respostas da parte intercaladas pela hora.
 *
 * O mapper agrupa por conteúdo idêntico: o mesmo texto de citação enviado ao requerente e ao
 * requerido vira UMA mensagem com dois destinatários. Isso servia à réplica antiga, uma moldura
 * por mensagem. A conversa é por destinatário, então a mesma mensagem entra na conversa de cada
 * um, com o status e o ID daquela pessoa.
 *
 * Uma parte com dois números tem duas conversas, como no WhatsApp. A ordem das conversas é a de
 * chegada — a mesma em que o mapper entrega as mensagens, que já seguem a ordem de envio.
 */
export function agruparConversasWhatsapp(
  mensagens: MensagemWhatsappCertificado[],
  respostas: RespostaWhatsappCertificado[] = [],
  remetente = '',
): ConversaWhatsappCertificado[] {
  const conversas = new Map<string, ConversaWhatsappCertificado>();

  mensagens.forEach((mensagem, indiceMensagem) => {
    for (const destinatario of mensagem.destinatarios) {
      const chave = chaveDaConversa(destinatario.parteArbitralId, destinatario.contato);
      const conversa = conversas.get(chave) ?? {
        chave,
        de: mensagem.de,
        nome: destinatario.nome,
        contato: destinatario.contato,
        bolhas: [],
      };
      conversa.bolhas.push(bolhaEnviada(mensagem, destinatario, indiceMensagem));
      conversas.set(chave, conversa);
    }
  });

  // O número desconhecido considera todos os telefones conhecidos, independentemente da ordem das respostas.
  respostas.forEach((resposta) => {
    if (resposta.contato) conversaDaResposta(conversas, resposta, mensagens[0]?.de ?? remetente);
  });

  const recebidasPorConversa = new Map<string, BolhaWhatsappCertificado[]>();
  respostas.forEach((resposta, indice) => {
    const conversa = conversaDaResposta(conversas, resposta, mensagens[0]?.de ?? remetente);
    const recebidas = recebidasPorConversa.get(conversa.chave) ?? [];
    recebidas.push(bolhaRecebida(resposta, conversa.chave, indice));
    recebidasPorConversa.set(conversa.chave, recebidas);
  });

  return [...conversas.values()].map((conversa) => ({
    ...conversa,
    bolhas: intercalarPorHorario(conversa.bolhas, recebidasPorConversa.get(conversa.chave) ?? []),
  }));
}
